import { BoxProps } from "@mui/material";
import { Box, Checkbox, FormControlLabel, GridColumn, SearchTextField } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { componentItemMatchesText } from "designSystem/utilities/component-item-functions";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { CnlsAndValues, ComponentFilterItem } from "../common";

export interface FilterDropdownListProps<T extends ComponentFilterItem> extends BoxProps {
	/** the id set which will be checked in the list */
	adHocIds?: string[];

	/** the ids to deselect */
	deselectIds?: string[];

	/** the id set which will be checked and disabled in the list */
	fixedIds?: string[];

	/** items for the details */
	items: T[];

	/** callback for checkbox value change */
	onChecked?: (cnlsAndValue: CnlsAndValues, checked: boolean) => void;

	/** callback when done with value change, would carry over all the checked items */
	postCheckItems?: (keys: string[]) => void;

	/** callback when done with reset */
	postReset?: () => void;

	/** indicate if reset */
	reset?: boolean;

	/** indicate if values should scroll */
	scroll?: boolean;

	/** indicate if show the count or not */
	showCount?: boolean;
}

const FilterDropdownList = <T extends ComponentFilterItem>(props: FilterDropdownListProps<T>) => {
	const {
		items,
		showCount,
		fixedIds,
		adHocIds,
		deselectIds,
		onChecked,
		postCheckItems,
		postReset,
		reset,
		scroll,
		...otherProps
	} = props;

	const loc = useLocale();
	const defaultList: string[] = [];

	if (adHocIds) {
		defaultList.push(...adHocIds);
	}

	const [checkedList, setCheckedList] = useState<string[]>(defaultList);
	const [values, setValues] = useState<T[]>(items);
	const [searchText, setSearchText] = useState<string>("");

	/** handle filter */
	const handleOnChange = (text: string) => {
		setSearchText(text);
		setValues(items.filter((item) => componentItemMatchesText(item, text, loc)));
	};

	// track the items
	useEffect(() => {
		setValues(items);
	}, [items]);

	// track the checked list made by user
	useEffect(() => {
		postCheckItems?.(checkedList);
	}, [checkedList]);

	// deselect items
	useEffect(() => {
		if (reset) {
			setCheckedList([]);
			postReset?.();
		} else if (deselectIds?.length > 0) {
			setCheckedList(_.difference(checkedList, deselectIds));
		}
	}, [deselectIds, reset]);

	/** handle the logic when check/uncheck  */
	const onCheckItem = (checked: boolean, item: T) => {
		const { id } = item;

		if (checked) {
			setCheckedList([...checkedList, id]);
		} else {
			if (checkedList.includes(id)) {
				checkedList.splice(checkedList.indexOf(id), 1);
				setCheckedList([...checkedList]);
			}
		}

		onChecked?.({ values: [item.value], cnls: item.cnls }, checked);
	};

	return (
		<Box
			display="flex"
			flexDirection="column"
			height="100%"
			pt={1}
			{...otherProps}
		>
			<Box pb={4}>
				<SearchTextField
					fullWidth
					onChange={(event) => handleOnChange(event.target.value)}
					placeholderRes="TypeKeywordToFilter"
					value={searchText}
				/>
			</Box>
			<GridColumn
				grow
				scroll={scroll}
				spacing={2}
			>
				{
					values.map((item) => {
						const countItem = item as unknown as ComponentFilterItem;

						const label = `${loc.getComponentItemName(item) || loc.resource("Blanks")}${showCount && !countItem.hideCount ? ` (${countItem.count})` : ""}`;
						const fixed = fixedIds?.includes(item.id);

						return (
							<FormControlLabel
								block
								checked={fixed || checkedList.includes(item.id)}
								control={<Checkbox />}
								disabled={fixed}
								key={item.id}
								label={label}
								labelPlacement="end"
								onChange={(event, checked) => onCheckItem(checked, item)}
								wrap={false}
							/>
						);
					})
				}
			</GridColumn>
		</Box>
	);
};

export default FilterDropdownList;